import { useAuth0 } from '@auth0/auth0-react';
import { useContext, useMemo, useRef, useState } from 'react';
import { generatePath, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { InviteModal } from '../../../app/components/account/invite-modal';
import { UserPreferences } from '../../../app/components/account/user-preferences-modal';
import ReloadPrompt from '../../../app/components/reload-prompt/reload-prompt';
import { Role } from '../../../app/constants/auth';
import { Paths, QueryParams } from '../../../app/constants/paths';
import { AuthorizationContext } from '../../../app/context/authorization-context';
import { useTenant } from '../../../app/context/tenant-context';
import UserPreferencesContextProvider from '../../../app/context/user-preferences-context';
import { useTheme } from '../../../app/hooks/accounts';
import logo from '../../../assets/cr-logo.svg';
import { useGetTenantConfig } from '../../hooks/tenants';

import { Invite, Logo, Menu, Nav, NavMenuItem, Profile, StyledSidebar, TooltipText } from './style';

import { Icon, Li, Modal, ModalContents, ModalOpenButton, Tooltip } from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';
import { useClickOutside } from '@controlrooms/hooks';

const Ul = styled.ul<{ isOpen: boolean }>`
  position: fixed;
  bottom: 0;
  left: 55px;
  flex-direction: column;
  justify-content: flex-start;
  width: 211px;
  padding: 0;
  margin-top: 25px;
  background-color: ${({ theme }) => theme.modal.contentBackgroundColor};
  list-style: none;
  border: 1px solid black;
  border-radius: 4px;
  ${(props) => {
    if (!props.isOpen) {
      return `
        display: none;
      `;
    }
  }}
`;

const StyledUserDropdown = styled(Ul)`
  li {
    margin: 0;
  }
  li > button {
    color: ${({ theme }) => theme.modal.panel.color};
    cursor: pointer;
    border: 0;
    background: transparent;
  }
`;

const Sidebar = () => {
  const { logout, user } = useAuth0();

  const { data: tenantConfig } = useGetTenantConfig();
  const [searchParams, setSearchParams] = useSearchParams();

  const [, themeName] = useTheme();
  const { tenant } = useTenant();
  const [isUserSelectOpen, setIsUserSelectOpen] = useState(false);
  const userUlRef = useRef(null);
  useClickOutside(userUlRef, () => setIsUserSelectOpen(false));
  const { canUserReadAlert, hasRole } = useContext(AuthorizationContext);

  const profileInitial = useMemo(() => {
    const name = user?.name || '';
    const initial =
      name
        .match(/(\b\S)?/g)
        ?.join('')
        ?.match(/(^\S|\S$)?/g)
        ?.join('')
        .toUpperCase() || 'U';
    return initial;
  }, [user?.name]);

  return (
    <StyledSidebar className="app-sidebar">
      <Logo className="logo" data-testid="cr-logo">
        <img src={logo} />
      </Logo>
      <Nav>
        <Menu>
          {hasRole(Role.ALERT_DASHBOARD_V2) && (
            <NavMenuItem
              to={generatePath(Paths.ALERT_BOARD, { tenantId: tenant.toString() })}
              data-testid="alert-board"
            >
              <Icon name={ICONS.Alert} color="#93bbc4" />
              <TooltipText>Alert Board</TooltipText>
            </NavMenuItem>
          )}
          <NavMenuItem
            end={true}
            to={generatePath(Paths.VIEWS, { tenantId: tenant.toString() })}
            data-testid="views"
          >
            <Icon name={ICONS.Views} color="#93bbc4" />
            <TooltipText>Views</TooltipText>
          </NavMenuItem>

          {hasRole(Role.ALERT_MANAGER_V2) && (
            <NavMenuItem
              to={generatePath(Paths.MANAGE_ALERTS, { tenantId: tenant.toString() })}
              data-testid="alert-manager"
            >
              <Icon name={ICONS.Gear} color="#93bbc4" />
              <TooltipText>Alert Manager</TooltipText>
            </NavMenuItem>
          )}
          {canUserReadAlert && (
            <NavMenuItem
              to={generatePath(Paths.MANAGE_LIMITS, { tenantId: tenant.toString() })}
              data-testid="limit-manager"
            >
              <Icon name={ICONS.Limits} color="#93bbc4" />
              <TooltipText>Limit Manager</TooltipText>
            </NavMenuItem>
          )}
          {(hasRole(Role.MLOPS) || hasRole(Role.TEAMS_ADMIN)) && (
            <NavMenuItem
              to={generatePath(Paths.MANAGE, { tenantId: tenant.toString() })}
              data-testid="manage"
            >
              <Icon name={ICONS.Manage} color="#93bbc4" />
              <TooltipText>Manage</TooltipText>
            </NavMenuItem>
          )}
        </Menu>
      </Nav>
      <Nav style={{ flex: 'none' }}>
        <ReloadPrompt />
        {tenantConfig?.showInvite && (
          <Modal open={Boolean(searchParams.get(QueryParams.INVITE))}>
            <ModalOpenButton>
              <Tooltip
                label="Invite a Colleague"
                place="right-end"
                theme={themeName.toLowerCase()}
                delay="100"
              >
                <Invite
                  buttonSize="large"
                  data-testid="invite-a-colleague"
                  style={{
                    backgroundColor: 'transparent',
                    width: '100%',
                    justifyContent: 'space-evenly',
                  }}
                  buttonType="icon"
                  iconName="export"
                  onClick={() => {
                    const params = new URLSearchParams();
                    params.append(QueryParams.INVITE, 'true');
                    params.append(QueryParams.ROLE, Role.OBSERVER);
                    return setSearchParams(params);
                  }}
                />
              </Tooltip>
            </ModalOpenButton>
            <ModalContents
              closeButtonCallback={() => {
                const _params = new URLSearchParams(searchParams);
                _params.delete(QueryParams.INVITE);
                _params.delete(QueryParams.ROLE);
                setSearchParams(_params);
              }}
              title="Invite"
            >
              <InviteModal
                sandbox={false}
                onSubmit={() => {
                  const _params = new URLSearchParams(searchParams);
                  _params.delete(QueryParams.INVITE);
                  _params.delete(QueryParams.ROLE);
                  setSearchParams(_params);
                }}
              />
            </ModalContents>
          </Modal>
        )}

        <Profile
          data-testid="user"
          buttonSize="small"
          buttonType="text"
          onClick={(evt) => {
            evt.stopPropagation();
            setIsUserSelectOpen((prevState) => !prevState);
          }}
        >
          {profileInitial}
        </Profile>
        <StyledUserDropdown isOpen={isUserSelectOpen} ref={userUlRef} className="user-dropdown">
          <Li data-testid="user-preferences" className="user-prefs-menu-item">
            <UserPreferencesContextProvider>
              <Modal>
                <ModalOpenButton>
                  <button>User Preferences</button>
                </ModalOpenButton>
                <ModalContents title="User Preferences" closeButtonText="Close">
                  <UserPreferences />
                </ModalContents>
              </Modal>
            </UserPreferencesContextProvider>
          </Li>
          <Li data-testid="log-out" id="logout" className="logout">
            <button
              onClick={() => {
                sessionStorage.clear();
                logout({ logoutParams: { returnTo: window.location.origin } });
              }}
            >
              Log Out
            </button>
          </Li>
        </StyledUserDropdown>
      </Nav>
    </StyledSidebar>
  );
};

export default Sidebar;
